import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Grid, Typography, Button, Chip, Avatar, 
  CircularProgress, LinearProgress, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, TextField, IconButton, Divider, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Rating, Alert
} from '@mui/material';
import {
  School as TrainingIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CompletedIcon,
  RadioButtonUnchecked as NotStartedIcon,
  YouTube as YouTubeIcon,
  Article as ArticleIcon,
  MenuBook as WikiIcon,
  Headphones as AudioIcon,
  Podcasts as PodcastIcon,
  Link as LinkIcon,
  PictureAsPdf as PdfIcon,
  PlayCircle as VideoIcon,
  GitHub as GithubIcon,
  Collections as SlidesIcon,
  Quiz as FlashcardIcon,
  Timer as TimerIcon,
  ArrowBack as BackIcon,
  Note as NoteIcon,
  Add as AddNoteIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  OpenInNew as OpenIcon,
  Done as DoneIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

// Content type icons
const contentTypeIcons = {
  youtube: YouTubeIcon,
  wiki: WikiIcon,
  medium: ArticleIcon,
  document: ArticleIcon,
  url: LinkIcon,
  video: VideoIcon,
  pdf: PdfIcon,
  udemy: VideoIcon,
  coursera: VideoIcon,
  flashcard: FlashcardIcon,
  audio: AudioIcon,
  podcast: PodcastIcon,
  reference: LinkIcon,
  article: ArticleIcon,
  ebook: WikiIcon,
  slides: SlidesIcon,
  github: GithubIcon
};

// Extract YouTube video ID from URL
const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const TrainingLearn = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrollment, setEnrollment] = useState(null);
  const [selectedProgress, setSelectedProgress] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [ratingDialog, setRatingDialog] = useState(false);
  const [rating, setRating] = useState(0);
  const [startTime, setStartTime] = useState(null);

  const fetchEnrollment = useCallback(async () => {
    try {
      const response = await api.get(`/training/user/enrollment/${enrollmentId}`);
      const data = response.data.data;
      setEnrollment(data);
      
      // Auto-select first incomplete item or first item
      if (data?.progress?.length > 0) {
        const nextItem = data.progress.find(p => p.status !== 'completed') || data.progress[0];
        setSelectedProgress(nextItem);
        setStartTime(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch enrollment:', error);
      toast.error('Failed to load training');
      navigate('/training/my-training');
    } finally {
      setLoading(false);
    }
  }, [enrollmentId, navigate]);

  useEffect(() => {
    fetchEnrollment();
  }, [fetchEnrollment]);

  // Track time when switching items
  useEffect(() => {
    const trackTime = async () => {
      if (selectedProgress && startTime) {
        const timeSpent = Math.round((new Date() - startTime) / 60000); // minutes
        if (timeSpent >= 1) {
          try {
            await api.put(`/training/user/progress/${selectedProgress.id}`, {
              time_spent_minutes: timeSpent
            });
          } catch (e) {
            console.error('Failed to track time:', e);
          }
        }
      }
    };

    return () => {
      trackTime();
    };
  }, [selectedProgress, startTime]);

  const handleSelectItem = (progress) => {
    // Track time for previous item
    if (selectedProgress && startTime) {
      const timeSpent = Math.round((new Date() - startTime) / 60000);
      if (timeSpent >= 1) {
        api.put(`/training/user/progress/${selectedProgress.id}`, {
          time_spent_minutes: timeSpent
        }).catch(console.error);
      }
    }
    
    setSelectedProgress(progress);
    setStartTime(new Date());
    
    // Mark as in progress if not started
    if (progress.status === 'not_started') {
      api.put(`/training/user/progress/${progress.id}`, {
        status: 'in_progress'
      }).then(fetchEnrollment).catch(console.error);
    }
  };

  const handleMarkComplete = async () => {
    if (!selectedProgress) return;
    setRatingDialog(true);
  };

  const handleConfirmComplete = async () => {
    setCompleting(true);
    try {
      await api.put(`/training/user/progress/${selectedProgress.id}`, {
        status: 'completed',
        rating: rating || undefined
      });
      toast.success('Item marked as complete!');
      setRatingDialog(false);
      setRating(0);
      fetchEnrollment();
    } catch (error) {
      toast.error('Failed to update progress');
    } finally {
      setCompleting(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedProgress) return;
    
    setAddingNote(true);
    try {
      await api.post(`/training/user/progress/${selectedProgress.id}/notes`, {
        note_text: noteText.trim()
      });
      toast.success('Note added!');
      setNoteText('');
      fetchEnrollment();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/training/user/notes/${noteId}`);
      toast.success('Note deleted');
      fetchEnrollment();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const renderContent = () => {
    if (!selectedProgress?.material) return null;
    
    const material = selectedProgress.material;
    const contentType = material.content_type;
    const contentUrl = material.content_url;

    // YouTube embed
    if (contentType === 'youtube') {
      const videoId = getYouTubeId(contentUrl);
      if (videoId) {
        return (
          <Box sx={{ position: 'relative', paddingTop: '56.25%', width: '100%' }}>
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: 8
              }}
              src={`https://www.youtube.com/embed/${videoId}`}
              title={material.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
        );
      }
    }

    // PDF embed
    if (contentType === 'pdf' && contentUrl) {
      return (
        <Box sx={{ height: 600, width: '100%' }}>
          <iframe
            src={contentUrl}
            style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
            title={material.title}
          />
        </Box>
      );
    }

    // External link preview
    return (
      <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
        {React.createElement(contentTypeIcons[contentType] || LinkIcon, { 
          sx: { fontSize: 64, color: 'primary.main', mb: 2 } 
        })}
        <Typography variant="h6" sx={{ mb: 2 }}>
          {material.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {material.description}
        </Typography>
        <Button
          variant="contained"
          startIcon={<OpenIcon />}
          href={contentUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="large"
        >
          Open {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
        </Button>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!enrollment) return null;

  const progressItems = enrollment.progress || [];
  const completedCount = progressItems.filter(p => p.status === 'completed').length;
  const currentNotes = selectedProgress?.notes || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/training/my-training')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {enrollment.category?.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={enrollment.progress_percentage || 0}
              sx={{ flex: 1, maxWidth: 200, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary">
              {completedCount}/{progressItems.length} completed ({enrollment.progress_percentage}%)
            </Typography>
          </Box>
        </Box>
        {enrollment.status === 'completed' && (
          <Chip icon={<CompletedIcon />} label="Completed" color="success" />
        )}
        {enrollment.status === 'completed' && (
          <Button
            variant="outlined"
            onClick={() =>
              navigate(
                `/training/catalog/category/${enrollment.category_id || enrollment.category?.id}?tab=feedback`
              )
            }
          >
            Give course feedback
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Sidebar - Materials List */}
        <Grid item xs={12} md={4} lg={3}>
          <Card sx={{ position: 'sticky', top: 80 }}>
            <CardContent sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ px: 1, py: 1, color: 'text.secondary' }}>
                Training Materials
              </Typography>
              <List dense>
                {progressItems.map((progress, index) => {
                  const material = progress.material;
                  const Icon = contentTypeIcons[material?.content_type] || LinkIcon;
                  const isSelected = selectedProgress?.id === progress.id;
                  const isCompleted = progress.status === 'completed';

                  return (
                    <ListItem key={progress.id} disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleSelectItem(progress)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          bgcolor: isSelected ? 'primary.50' : 'transparent'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {isCompleted ? (
                            <CompletedIcon color="success" sx={{ fontSize: 20 }} />
                          ) : (
                            <Typography variant="caption" sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              border: '2px solid',
                              borderColor: 'grey.400',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {index + 1}
                            </Typography>
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={material?.title}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Icon sx={{ fontSize: 12 }} />
                              <span style={{ textTransform: 'capitalize' }}>
                                {material?.content_type}
                              </span>
                              {material?.duration_minutes && (
                                <span> • {formatDuration(material.duration_minutes)}</span>
                              )}
                            </Box>
                          }
                          primaryTypographyProps={{ 
                            variant: 'body2', 
                            fontWeight: isSelected ? 600 : 400,
                            noWrap: true
                          }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={8} lg={9}>
          {selectedProgress ? (
            <Box>
              {/* Content Header */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedProgress.material?.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip 
                          size="small"
                          icon={React.createElement(contentTypeIcons[selectedProgress.material?.content_type] || LinkIcon, { sx: { fontSize: 14 } })}
                          label={selectedProgress.material?.content_type}
                          sx={{ textTransform: 'capitalize' }}
                        />
                        <Chip 
                          size="small"
                          label={selectedProgress.material?.difficulty_level}
                          color={selectedProgress.material?.difficulty_level === 'beginner' ? 'success' : selectedProgress.material?.difficulty_level === 'intermediate' ? 'warning' : 'error'}
                        />
                        {selectedProgress.material?.duration_minutes && (
                          <Chip 
                            size="small"
                            icon={<TimerIcon sx={{ fontSize: 14 }} />}
                            label={formatDuration(selectedProgress.material.duration_minutes)}
                          />
                        )}
                      </Box>
                    </Box>
                    {selectedProgress.status !== 'completed' ? (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<DoneIcon />}
                        onClick={handleMarkComplete}
                      >
                        Mark Complete
                      </Button>
                    ) : (
                      <Chip icon={<CompletedIcon />} label="Completed" color="success" />
                    )}
                  </Box>
                  {selectedProgress.material?.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      {selectedProgress.material.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Content Viewer */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  {renderContent()}
                </CardContent>
              </Card>

              {/* Notes Section */}
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NoteIcon />
                    My Notes
                  </Typography>

                  {/* Add Note */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Add your notes here..."
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddNote}
                      disabled={!noteText.trim() || addingNote}
                      sx={{ alignSelf: 'flex-end' }}
                    >
                      {addingNote ? <CircularProgress size={20} /> : <AddNoteIcon />}
                    </Button>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Notes List */}
                  {currentNotes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No notes yet. Add your first note above!
                    </Typography>
                  ) : (
                    <List>
                      {currentNotes.map((note) => (
                        <ListItem
                          key={note.id}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => handleDeleteNote(note.id)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          }
                          sx={{ bgcolor: 'grey.50', borderRadius: 1, mb: 1 }}
                        >
                          <ListItemText
                            primary={note.note_text}
                            secondary={new Date(note.createdAt).toLocaleString()}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Select a training item from the sidebar to begin
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Rating Dialog */}
      <Dialog open={ratingDialog} onClose={() => setRatingDialog(false)}>
        <DialogTitle>Rate this training item</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Rating
              value={rating}
              onChange={(e, newValue) => setRating(newValue)}
              size="large"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              (Optional) How helpful was this material?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRatingDialog(false); setRating(0); }}>
            Skip
          </Button>
          <Button 
            variant="contained" 
            onClick={handleConfirmComplete}
            disabled={completing}
          >
            {completing ? 'Saving...' : 'Complete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingLearn;
